import { IHumanResourcesRepository } from './IHumanResourcesRepository';
import {
  TemporalCompany,
  CreateTemporalCompanyInput,
  UpdateTemporalCompanyInput,
  Position,
  CreatePositionInput,
  UpdatePositionInput,
  Employee,
  CreateEmployeeInput,
  UpdateEmployeeInput,
  ShiftRecord,
  CreateShiftRecordInput,
  UpdateShiftRecordInput,
  ShiftRecordFilters,
  PagedResult,
  RateFactor,
  CreateRateFactorInput,
  UpdateRateFactorInput,
  CalendarDay,
  CreateCalendarDayInput,
  UpdateCalendarDayInput,
  ListCalendarDaysFilters,
  CalendarStagingRow,
  CreateStagingBatchInput,
  UpdateStagingRowInput,
  ListStagingFilters,
  SummaryRow,
} from './types';
import { computeShiftPayLocal, makeRateResolverLocal, DEFAULT_LOCAL_RATE_FACTORS } from './localCalc';

const EMPLOYEES_KEY = 'lcc.hr.employees';
const TEMPORAL_COMPANIES_KEY = 'lcc.hr.temporalCompanies';
const POSITIONS_KEY = 'lcc.hr.positions';
const SHIFTS_KEY = 'lcc.hr.shiftRecords';
const RATE_FACTORS_KEY = 'lcc.hr.rateFactors';
const CALENDAR_DAYS_KEY = 'lcc.hr.calendarDays';
const STAGING_KEY = 'lcc.hr.calendarStaging';

const WEEKDAY_NAMES_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTH_NAMES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const SEED_EMPLOYEES: Employee[] = [
  { id: crypto.randomUUID(), documentNumber: '1002003004', fullName: 'CARLOS RAMIREZ', temporalCompany: 'OCUPAR', position: 'Auxiliar de Bodega', monthlySalary: 1750905, isActive: true },
  { id: crypto.randomUUID(), documentNumber: '1002003005', fullName: 'DIANA GOMEZ', temporalCompany: 'OSYA', position: 'Auxiliar de Despacho', monthlySalary: 1900000, isActive: true },
];

const SEED_TEMPORAL_COMPANIES: TemporalCompany[] = [
  { id: crypto.randomUUID(), name: 'OCUPAR', isActive: true },
  { id: crypto.randomUUID(), name: 'OSYA', isActive: true },
  { id: crypto.randomUUID(), name: 'L&S', isActive: true },
];

const SEED_POSITIONS: Position[] = [
  { id: crypto.randomUUID(), name: 'AUXILIAR OPERATIVO', isActive: true },
  { id: crypto.randomUUID(), name: 'AUXILIAR ADMINISTRATIVO', isActive: true },
  { id: crypto.randomUUID(), name: 'AUXILIAR ADMINISTRATIVO-OPERATIVO', isActive: true },
  { id: crypto.randomUUID(), name: 'EXTENSION DE HORARIO', isActive: true },
];

function readOrSeed<T>(key: string, seed: T[]): T[] {
  const raw = localStorage.getItem(key);
  if (raw) return JSON.parse(raw);
  localStorage.setItem(key, JSON.stringify(seed));
  return seed;
}
function write<T>(key: string, data: T[]) {
  localStorage.setItem(key, JSON.stringify(data));
}

/** Implementacion local (modo desarrollo sin backend) del modulo Horas Extra. */
export class LocalHumanResourcesRepository implements IHumanResourcesRepository {
  async listTemporalCompanies(includeInactive = false): Promise<TemporalCompany[]> {
    const all = readOrSeed(TEMPORAL_COMPANIES_KEY, SEED_TEMPORAL_COMPANIES);
    return includeInactive ? all : all.filter((c) => c.isActive);
  }
  async createTemporalCompany(input: CreateTemporalCompanyInput): Promise<TemporalCompany> {
    const all = readOrSeed(TEMPORAL_COMPANIES_KEY, SEED_TEMPORAL_COMPANIES);
    const item: TemporalCompany = { id: crypto.randomUUID(), name: input.name.toUpperCase().trim(), isActive: true };
    all.push(item);
    write(TEMPORAL_COMPANIES_KEY, all);
    return item;
  }
  async updateTemporalCompany(id: string, patch: UpdateTemporalCompanyInput): Promise<TemporalCompany> {
    const all = readOrSeed(TEMPORAL_COMPANIES_KEY, SEED_TEMPORAL_COMPANIES);
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Empresa temporal no encontrada');
    all[idx] = { ...all[idx], ...patch, name: patch.name ? patch.name.toUpperCase().trim() : all[idx].name };
    write(TEMPORAL_COMPANIES_KEY, all);
    return all[idx];
  }
  async deleteTemporalCompany(id: string): Promise<void> {
    const all = readOrSeed(TEMPORAL_COMPANIES_KEY, SEED_TEMPORAL_COMPANIES);
    const idx = all.findIndex((c) => c.id === id);
    if (idx >= 0) { all[idx].isActive = false; write(TEMPORAL_COMPANIES_KEY, all); }
  }

  async listPositions(includeInactive = false): Promise<Position[]> {
    const all = readOrSeed(POSITIONS_KEY, SEED_POSITIONS);
    return includeInactive ? all : all.filter((p) => p.isActive);
  }
  async createPosition(input: CreatePositionInput): Promise<Position> {
    const all = readOrSeed(POSITIONS_KEY, SEED_POSITIONS);
    const item: Position = { id: crypto.randomUUID(), name: input.name.toUpperCase().trim(), isActive: true };
    all.push(item);
    write(POSITIONS_KEY, all);
    return item;
  }
  async updatePosition(id: string, patch: UpdatePositionInput): Promise<Position> {
    const all = readOrSeed(POSITIONS_KEY, SEED_POSITIONS);
    const idx = all.findIndex((p) => p.id === id);
    if (idx === -1) throw new Error('Cargo no encontrado');
    all[idx] = { ...all[idx], ...patch, name: patch.name ? patch.name.toUpperCase().trim() : all[idx].name };
    write(POSITIONS_KEY, all);
    return all[idx];
  }
  async deletePosition(id: string): Promise<void> {
    const all = readOrSeed(POSITIONS_KEY, SEED_POSITIONS);
    const idx = all.findIndex((p) => p.id === id);
    if (idx >= 0) { all[idx].isActive = false; write(POSITIONS_KEY, all); }
  }

  async listEmployees(includeInactive = false): Promise<Employee[]> {
    const all = readOrSeed(EMPLOYEES_KEY, SEED_EMPLOYEES);
    return includeInactive ? all : all.filter((e) => e.isActive);
  }
  async createEmployee(input: CreateEmployeeInput): Promise<Employee> {
    const all = readOrSeed(EMPLOYEES_KEY, SEED_EMPLOYEES);
    const employee: Employee = {
      id: crypto.randomUUID(),
      documentNumber: input.documentNumber,
      fullName: input.fullName.toUpperCase(),
      temporalCompany: input.temporalCompany ?? null,
      position: input.position ?? null,
      monthlySalary: input.monthlySalary,
      isActive: true,
    };
    all.push(employee);
    write(EMPLOYEES_KEY, all);
    return employee;
  }
  async updateEmployee(id: string, patch: UpdateEmployeeInput): Promise<Employee> {
    const all = readOrSeed(EMPLOYEES_KEY, SEED_EMPLOYEES);
    const idx = all.findIndex((e) => e.id === id);
    if (idx === -1) throw new Error('Empleado no encontrado');
    all[idx] = { ...all[idx], ...patch, fullName: patch.fullName ? patch.fullName.toUpperCase() : all[idx].fullName };
    write(EMPLOYEES_KEY, all);
    return all[idx];
  }
  async deleteEmployee(id: string): Promise<void> {
    const all = readOrSeed(EMPLOYEES_KEY, SEED_EMPLOYEES);
    const idx = all.findIndex((e) => e.id === id);
    if (idx >= 0) { all[idx].isActive = false; write(EMPLOYEES_KEY, all); }
  }

  async listShiftRecords(filters: ShiftRecordFilters): Promise<PagedResult<ShiftRecord>> {
    let all = readOrSeed<ShiftRecord>(SHIFTS_KEY, []);
    if (filters.employeeId) all = all.filter((s) => s.employeeId === filters.employeeId);
    if (filters.dateFrom) all = all.filter((s) => s.shiftDate >= filters.dateFrom!);
    if (filters.dateTo) all = all.filter((s) => s.shiftDate <= filters.dateTo!);
    all = all.slice().sort((a, b) => b.shiftDate.localeCompare(a.shiftDate));
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 50;
    const start = (page - 1) * pageSize;
    return { items: all.slice(start, start + pageSize), total: all.length, page, pageSize };
  }
  async createShiftRecord(input: CreateShiftRecordInput): Promise<ShiftRecord> {
    const all = readOrSeed<ShiftRecord>(SHIFTS_KEY, []);
    const employees = await this.listEmployees(true);
    const employee = employees.find((e) => e.id === input.employeeId);
    const dayTypeMap = await this.getDayTypeMap();
    const isFestive = dayTypeMap.get(input.shiftDate) === 'Festivo';
    const record: ShiftRecord = {
      id: crypto.randomUUID(),
      employeeId: input.employeeId,
      employeeName: employee?.fullName ?? '(empleado no encontrado)',
      shiftDate: input.shiftDate,
      assignedEntryTime: input.assignedEntryTime,
      assignedHours: input.assignedHours,
      operativeEntryTime: input.operativeEntryTime,
      operativeHours: input.operativeHours,
      mealHours: input.mealHours ?? 0,
      hasCompensatoryRest: input.hasCompensatoryRest ?? null,
      incapacity: input.incapacity ?? null,
      unpaidLeave: input.unpaidLeave ?? null,
      paidLeave: input.paidLeave ?? null,
      noveltyReason: input.noveltyReason ?? null,
      authorizedOrDeducted: input.authorizedOrDeducted ?? null,
      isFestive,
    };
    all.push(record);
    write(SHIFTS_KEY, all);
    return record;
  }
  async updateShiftRecord(id: string, patch: UpdateShiftRecordInput): Promise<ShiftRecord> {
    const all = readOrSeed<ShiftRecord>(SHIFTS_KEY, []);
    const idx = all.findIndex((s) => s.id === id);
    if (idx === -1) throw new Error('Registro no encontrado');
    all[idx] = { ...all[idx], ...patch } as ShiftRecord;
    write(SHIFTS_KEY, all);
    return all[idx];
  }
  async deleteShiftRecord(id: string): Promise<void> {
    write(SHIFTS_KEY, readOrSeed<ShiftRecord>(SHIFTS_KEY, []).filter((s) => s.id !== id));
  }

  // -----------------------------------------------------------------
  // Factores legales
  // -----------------------------------------------------------------
  async listRateFactors(): Promise<RateFactor[]> {
    return readOrSeed(RATE_FACTORS_KEY, DEFAULT_LOCAL_RATE_FACTORS);
  }
  async createRateFactor(input: CreateRateFactorInput): Promise<RateFactor> {
    const all = readOrSeed(RATE_FACTORS_KEY, DEFAULT_LOCAL_RATE_FACTORS);
    const row: RateFactor = { id: crypto.randomUUID(), effectiveTo: null, legalReference: null, ...input };
    all.push(row);
    write(RATE_FACTORS_KEY, all);
    return row;
  }
  async updateRateFactor(id: string, patch: UpdateRateFactorInput): Promise<RateFactor> {
    const all = readOrSeed(RATE_FACTORS_KEY, DEFAULT_LOCAL_RATE_FACTORS);
    const idx = all.findIndex((r) => r.id === id);
    if (idx === -1) throw new Error('Factor no encontrado');
    all[idx] = { ...all[idx], ...patch };
    write(RATE_FACTORS_KEY, all);
    return all[idx];
  }
  async deleteRateFactor(id: string): Promise<void> {
    write(RATE_FACTORS_KEY, readOrSeed(RATE_FACTORS_KEY, DEFAULT_LOCAL_RATE_FACTORS).filter((r) => r.id !== id));
  }

  // -----------------------------------------------------------------
  // Calendario FINAL (equivalente literal a DIAS de PARAMETROS)
  // -----------------------------------------------------------------
  async listCalendarDays(filters: ListCalendarDaysFilters): Promise<CalendarDay[]> {
    let all = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
    if (filters.year) all = all.filter((d) => d.calendarDate.startsWith(String(filters.year)));
    if (filters.month) all = all.filter((d) => Number(d.calendarDate.slice(5, 7)) === filters.month);
    if (filters.dayType) all = all.filter((d) => d.dayType === filters.dayType);
    return all.slice().sort((a, b) => a.calendarDate.localeCompare(b.calendarDate));
  }
  async createCalendarDay(input: CreateCalendarDayInput): Promise<CalendarDay> {
    const all = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
    const row: CalendarDay = { id: crypto.randomUUID(), ...input };
    all.push(row);
    write(CALENDAR_DAYS_KEY, all);
    return row;
  }
  async createCalendarDaysBulk(inputs: CreateCalendarDayInput[]): Promise<{ inserted: number; skipped: number }> {
    let inserted = 0; let skipped = 0;
    for (const input of inputs) {
      const all = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
      if (all.some((d) => d.calendarDate === input.calendarDate)) { skipped++; continue; }
      const row: CalendarDay = { id: crypto.randomUUID(), ...input };
      write(CALENDAR_DAYS_KEY, [...all, row]);
      inserted++;
    }
    return { inserted, skipped };
  }
  async updateCalendarDay(id: string, patch: UpdateCalendarDayInput): Promise<CalendarDay> {
    const all = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
    const idx = all.findIndex((d) => d.id === id);
    if (idx === -1) throw new Error('Día no encontrado');
    all[idx] = { ...all[idx], ...patch };
    write(CALENDAR_DAYS_KEY, all);
    return all[idx];
  }
  async deleteCalendarDay(id: string): Promise<void> {
    write(CALENDAR_DAYS_KEY, readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []).filter((d) => d.id !== id));
  }
  async deleteCalendarYear(year: number): Promise<number> {
    const all = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
    const remaining = all.filter((d) => !d.calendarDate.startsWith(String(year)));
    const deletedCount = all.length - remaining.length;
    write(CALENDAR_DAYS_KEY, remaining);
    return deletedCount;
  }

  private async getDayTypeMap(): Promise<Map<string, 'Ordinario' | 'Festivo'>> {
    const days = await this.listCalendarDays({});
    return new Map(days.map((d) => [d.calendarDate, d.dayType]));
  }

  // -----------------------------------------------------------------
  // Staging de importacion (carga por imagen)
  // -----------------------------------------------------------------
  async listStaging(filters: ListStagingFilters): Promise<CalendarStagingRow[]> {
    let all = readOrSeed<CalendarStagingRow>(STAGING_KEY, []);
    if (filters.importBatchId) all = all.filter((s) => s.importBatchId === filters.importBatchId);
    if (filters.sourceYear) all = all.filter((s) => s.sourceYear === filters.sourceYear);
    if (filters.sourceMonth) all = all.filter((s) => s.sourceMonth === filters.sourceMonth);
    if (filters.status) all = all.filter((s) => s.status === filters.status);
    return all.slice().sort((a, b) => a.calendarDate.localeCompare(b.calendarDate));
  }
  async createStagingBatch(input: CreateStagingBatchInput): Promise<{ importBatchId: string; rows: CalendarStagingRow[] }> {
    const all = readOrSeed<CalendarStagingRow>(STAGING_KEY, []);
    const importBatchId = crypto.randomUUID();
    const rows: CalendarStagingRow[] = input.rows.map((r) => ({
      id: crypto.randomUUID(),
      importBatchId,
      sourceYear: input.sourceYear,
      sourceMonth: input.sourceMonth,
      status: 'No revisado',
      confidence: r.confidence ?? null,
      ...r,
    }));
    write(STAGING_KEY, [...all, ...rows]);
    return { importBatchId, rows };
  }
  async updateStagingRow(id: string, patch: UpdateStagingRowInput): Promise<CalendarStagingRow> {
    const all = readOrSeed<CalendarStagingRow>(STAGING_KEY, []);
    const idx = all.findIndex((s) => s.id === id);
    if (idx === -1) throw new Error('Fila no encontrada');
    all[idx] = { ...all[idx], ...patch };
    write(STAGING_KEY, all);
    return all[idx];
  }
  async deleteStagingRow(id: string): Promise<void> {
    write(STAGING_KEY, readOrSeed<CalendarStagingRow>(STAGING_KEY, []).filter((s) => s.id !== id));
  }
  async confirmMonth(sourceYear: number, sourceMonth: number): Promise<number> {
    const staging = readOrSeed<CalendarStagingRow>(STAGING_KEY, []);
    const rowsToConfirm = staging.filter((s) => s.sourceYear === sourceYear && s.sourceMonth === sourceMonth);
    if (rowsToConfirm.length === 0) return 0;

    const calendarDays = readOrSeed<CalendarDay>(CALENDAR_DAYS_KEY, []);
    for (const row of rowsToConfirm) {
      const idx = calendarDays.findIndex((d) => d.calendarDate === row.calendarDate);
      const dayEntry: CalendarDay = { id: idx >= 0 ? calendarDays[idx].id : crypto.randomUUID(), calendarDate: row.calendarDate, dayOfWeek: row.dayOfWeek, dayType: row.dayType, quincena: row.quincena };
      if (idx >= 0) calendarDays[idx] = dayEntry;
      else calendarDays.push(dayEntry);
    }
    write(CALENDAR_DAYS_KEY, calendarDays);
    write(STAGING_KEY, staging.map((s) => (s.sourceYear === sourceYear && s.sourceMonth === sourceMonth ? { ...s, status: 'Procesado' as const } : s)));
    return rowsToConfirm.length;
  }

  async getSummary(dateFrom: string, dateTo: string, employeeId?: string): Promise<SummaryRow[]> {
    const [shifts, employees, rateFactors, dayTypeMap] = await Promise.all([
      this.listShiftRecords({ employeeId, dateFrom, dateTo, page: 1, pageSize: 1000 }),
      this.listEmployees(true),
      this.listRateFactors(),
      this.getDayTypeMap(),
    ]);
    const resolveRate = makeRateResolverLocal(rateFactors);
    const byEmployee = new Map<string, { assigned: number; operative: number; pay: ReturnType<typeof computeShiftPayLocal>[] }>();
    for (const shift of shifts.items) {
      const employee = employees.find((e) => e.id === shift.employeeId);
      if (!employee) continue;
      const pay = computeShiftPayLocal(shift, employee.monthlySalary, dayTypeMap, resolveRate);
      const bucket = byEmployee.get(shift.employeeId) ?? { assigned: 0, operative: 0, pay: [] };
      bucket.assigned += shift.assignedHours;
      bucket.operative += shift.operativeHours;
      bucket.pay.push(pay);
      byEmployee.set(shift.employeeId, bucket);
    }
    const rows: SummaryRow[] = [];
    for (const [empId, bucket] of byEmployee) {
      const employee = employees.find((e) => e.id === empId)!;
      const sum = (fn: (p: (typeof bucket.pay)[number]) => number) => bucket.pay.reduce((acc, p) => acc + fn(p), 0);
      rows.push({
        employeeId: empId,
        documentNumber: employee.documentNumber,
        employeeFullName: employee.fullName,
        temporalCompany: employee.temporalCompany,
        position: employee.position,
        monthlySalary: employee.monthlySalary,
        hourlyRate: bucket.pay[0]?.hourlyRate ?? 0,
        assignedHours: bucket.assigned,
        operativeHours: bucket.operative,
        extraDiurnalOrdinaryHours: sum((p) => p.extraDiurnalOrdinary),
        extraNocturnalOrdinaryHours: sum((p) => p.extraNocturnalOrdinary),
        extraDiurnalFestiveHours: sum((p) => p.extraDiurnalFestive),
        extraNocturnalFestiveHours: sum((p) => p.extraNocturnalFestive),
        extraDiurnalOrdinaryPay: 0,
        extraNocturnalOrdinaryPay: 0,
        extraDiurnalFestivePay: 0,
        extraNocturnalFestivePay: 0,
        totalExtraHours: sum((p) => p.extraDiurnalOrdinary + p.extraNocturnalOrdinary + p.extraDiurnalFestive + p.extraNocturnalFestive),
        totalExtraPay: 0,
        nightSurchargeHours: sum((p) => p.nightSurcharge),
        nightSurchargePay: 0,
        nightFestiveWithCompHours: sum((p) => p.nightFestiveWithComp),
        nightFestiveWithoutCompHours: sum((p) => p.nightFestiveWithoutComp),
        nightFestiveWithCompPay: 0,
        nightFestiveWithoutCompPay: 0,
        totalSurchargeHours: sum((p) => p.nightSurcharge + p.nightFestiveWithComp + p.nightFestiveWithoutComp),
        totalSurchargePay: 0,
        festiveWithCompHours: sum((p) => p.festiveWithComp),
        festiveWithoutCompHours: sum((p) => p.festiveWithoutComp),
        festiveWithCompPay: 0,
        festiveWithoutCompPay: 0,
        totalPay: sum((p) => p.totalPay),
      });
    }
    rows.sort((a, b) => a.employeeFullName.localeCompare(b.employeeFullName));
    return rows;
  }
}
